import type { ActivityMessage } from "@/api/models"

/**
 * Activity types the client synthesizes locally (never present in the server
 * `_activities` snapshot or typical `message_saved` payloads). Authoritative
 * server replaces must keep these or Approach B confirmation (#2469) vanishes
 * from Activity History as soon as the next snapshot lands.
 */
export const CLIENT_INJECTED_ACTIVITY_TYPES = new Set(["steer_applied"])

export function isClientInjectedActivity(activity: ActivityMessage): boolean {
  return CLIENT_INJECTED_ACTIVITY_TYPES.has(activity.activity_type)
}

function activityKey(activity: ActivityMessage): string {
  return `${activity.activity_type}\0${activity.message}\0${activity.timestamp}`
}

/**
 * Merge an authoritative server activities list with any client-injected rows
 * already on the live answer. Server entries win for server types;
 * client-injected rows (e.g. `steer_applied`) that the snapshot omitted are
 * re-attached and sorted by timestamp so they stay chronological in the accordion.
 */
export function mergeActivitiesPreservingClientInjected(
  serverActivities: ActivityMessage[],
  existingActivities: ActivityMessage[] | undefined | null
): ActivityMessage[] {
  const existing = existingActivities ?? []
  const clientInjected = existing.filter(isClientInjectedActivity)
  if (clientInjected.length === 0) {
    return serverActivities
  }

  const serverKeys = new Set(serverActivities.map(activityKey))
  const missing = clientInjected.filter((activity) => !serverKeys.has(activityKey(activity)))
  if (missing.length === 0) {
    return serverActivities
  }

  return [...serverActivities, ...missing].sort((a, b) => a.timestamp - b.timestamp)
}
